import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Users,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search,
  Sparkles,
} from 'lucide-react'
import Button from '@/components/common/Button'
import Card from '@/components/common/Card'
import Select from '@/components/common/Select'
import ProspectCard from '@/components/recruiter/prospects/ProspectCard'
import ProspectPipelineStats from '@/components/recruiter/prospects/ProspectPipelineStats'
import { useJobProspects } from '@/hooks/useJobProspects'
import recruiterJobService from '@/services/recruiterJobService'
import { toast } from '@/stores/toastStore'
import type { RecruiterJob, ProspectSource } from '@/types'

const SOURCE_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'SEARCH', label: 'From Search' },
  { value: 'AI_MATCH', label: 'From AI Match' },
]

const LIMIT = 20

export default function JobProspectsPage() {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()

  const [job, setJob] = useState<RecruiterJob | null>(null)
  const [loadingJob, setLoadingJob] = useState(true)
  const [sourceFilter, setSourceFilter] = useState<ProspectSource | ''>('')
  const [offerStatusFilter, setOfferStatusFilter] = useState<string | undefined>(undefined)
  const [page, setPage] = useState(1)

  useEffect(() => {
    if (!jobId) return
    recruiterJobService
      .getJob(jobId)
      .then(setJob)
      .catch(() => {
        toast.error('Failed to load job')
        navigate('/recruiter/jobs')
      })
      .finally(() => setLoadingJob(false))
  }, [jobId, navigate])

  const { data, isLoading } = useJobProspects(
    {
      recruiterJobId: jobId || '',
      source: sourceFilter || undefined,
      offerStatus: offerStatusFilter,
      page,
      limit: LIMIT,
    },
    !!jobId
  )

  const deduplicatedProspects = useMemo(() => {
    if (!data?.prospects) return []
    const grouped = new Map<
      string,
      {
        prospect: (typeof data.prospects)[number]
        offers: { id: string; status: string; createdAt: string }[]
      }
    >()
    for (const p of data.prospects) {
      const existing = grouped.get(p.candidateId)
      const offer = p.offer ? [p.offer] : []
      const extraOffers = p.offers ?? []
      const allOffers = [...offer, ...extraOffers]
      if (!existing) {
        grouped.set(p.candidateId, { prospect: p, offers: allOffers })
      } else {
        existing.offers.push(...allOffers)
        if (p.updatedAt > existing.prospect.updatedAt) {
          existing.prospect = p
        }
      }
    }
    return Array.from(grouped.values())
  }, [data])

  if (loadingJob) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <div className="container-custom py-8 max-w-4xl">
      {/* Back navigation */}
      <Button
        variant="secondary"
        onClick={() => navigate(`/recruiter/jobs/${jobId}`)}
        className="mb-6 flex items-center gap-2"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Job
      </Button>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{job?.title}</h1>
        <p className="text-gray-500 mt-1 flex items-center gap-2">
          <Users className="w-4 h-4" />
          Prospects Pipeline
        </p>
      </div>

      {/* Pipeline Stats */}
      <Card className="mb-6">
        <ProspectPipelineStats
          activeOfferStatusFilter={offerStatusFilter}
          onOfferStatusFilterChange={setOfferStatusFilter}
        />
      </Card>

      {/* Source Filter */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-48">
          <Select
            value={sourceFilter}
            onChange={(e) => {
              setSourceFilter(e.target.value as ProspectSource | '')
              setPage(1)
            }}
            options={SOURCE_OPTIONS}
          />
        </div>
        {data && (
          <p className="text-sm text-gray-500">
            {data.pagination.total} prospect{data.pagination.total !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Prospects List */}
      {isLoading ? (
        <div className="flex justify-center items-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : deduplicatedProspects.length === 0 ? (
        <Card className="text-center py-16">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No prospects yet</h3>
          <p className="text-gray-500 mb-6">
            {offerStatusFilter || sourceFilter
              ? 'No prospects match the current filter.'
              : 'Add candidates to build your pipeline.'}
          </p>
          {offerStatusFilter || sourceFilter ? (
            <Button
              variant="secondary"
              onClick={() => {
                setOfferStatusFilter(undefined)
                setSourceFilter('')
              }}
            >
              Clear Filters
            </Button>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="secondary"
                icon={<Search className="w-4 h-4" />}
                onClick={() => navigate('/recruiter/search')}
              >
                Search Candidates
              </Button>
              <Button
                variant="primary"
                icon={<Sparkles className="w-4 h-4" />}
                onClick={() => navigate('/recruiter/ai-matching')}
              >
                AI Matching
              </Button>
            </div>
          )}
        </Card>
      ) : (
        <>
          <div className="space-y-3 mb-6">
            {deduplicatedProspects.map(({ prospect, offers }) => (
              <ProspectCard
                key={prospect.candidateId}
                prospect={prospect}
                allOffers={offers}
                jobId={jobId}
              />
            ))}
          </div>

          {/* Pagination */}
          {data?.pagination.totalPages && data.pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => p - 1)}
                disabled={page === 1 || isLoading}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {page} of {data?.pagination.totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= (data?.pagination.totalPages ?? 1) || isLoading}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
